// src/components/inventory/InventoryParameterForm.tsx

import React, { useState } from "react";
import { InventoryParameter } from "../../types/Inventory";
import {
    CategoryParameter,
    ParameterDataType,
    SelectionType,
} from "../../types/Category";
import { Unit } from "../../types/Unit";
import { logApiError, getUserMessage } from "../../api/apiError";

interface InventoryParameterFormProps {
    parameter: InventoryParameter;
    availableParameters: CategoryParameter[]; // metadata from category
    units?: Unit[];
    isNew: boolean;                            // true = adding, false = editing
    onFieldChange: (field: keyof InventoryParameter, value: any) => void;
    onSave: () => Promise<void>;
    onCancel: () => void;
    loading: boolean;
}

const InventoryParameterForm: React.FC<InventoryParameterFormProps> = ({
                                                                           parameter,
                                                                           availableParameters,
                                                                           units = [],
                                                                           isNew,
                                                                           onFieldChange,
                                                                           onSave,
                                                                           onCancel,
                                                                           loading,
                                                                       }) => {
    const [saveError, setSaveError] = useState<string | null>(null);

    const toBoolean = (v: any): boolean => {
        if (typeof v === "boolean") return v;
        if (typeof v === "number") return v !== 0;
        if (typeof v === "string") {
            const s = v.trim().toLowerCase();
            return s === "true" || s === "1" || s === "yes";
        }
        return false;
    };

    // Allowed units for a DOUBLE param. Currency params (non-convertible dimension)
    // aren't pinned to specific currencies — offer every currency in the dimension,
    // resolved live from the units list so newly added currencies appear too.
    const allowedUnitsFor = (meta?: CategoryParameter): string[] => {
        if (!meta) return [];
        const raw = meta.unitIds ?? (meta.unitId ? [meta.unitId] : []);
        if (meta.convertible === false && raw[0]) {
            const dimId = units.find(u => u.id === raw[0])?.unitDimensionId;
            if (dimId) return units.filter(u => u.unitDimensionId === dimId).map(u => u.id);
        }
        return raw;
    };

    // Default currency/unit: prefer USD when present (later: user's profile default).
    const pickDefaultUnit = (ids: string[]): string =>
        ids.find(id => units.find(u => u.id === id)?.name?.toUpperCase() === "USD") ?? ids[0] ?? "";

    // When user chooses a parameter name from dropdown (add mode)
    const handleNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedName = e.target.value;
        onFieldChange("name", selectedName);

        const meta = availableParameters.find((p) => p.name === selectedName);
        if (!meta) return;

        onFieldChange("id", meta.id);
        // silently set dataType from category metadata
        onFieldChange("dataType", meta.dataType as ParameterDataType);

        // minimal: set default value based on the type
        if (meta.dataType === ParameterDataType.BOOLEAN) {
            onFieldChange("value", false);
        } else if (meta.dataType === ParameterDataType.SELECTABLE) {
            const st = (meta as any).selectionType ?? SelectionType.SINGLE;
            onFieldChange("selectionType", st);
            onFieldChange("value", st === SelectionType.MULTI ? [] : "");
        } else {
            onFieldChange("value", "");
            // Numeric: default the unit. DOUBLE picks the first allowed unit;
            // INTEGER uses its single (optional) unit.
            if (meta.dataType === ParameterDataType.DOUBLE) {
                onFieldChange("units", pickDefaultUnit(allowedUnitsFor(meta)));
            } else if (meta.dataType === ParameterDataType.INTEGER) {
                onFieldChange("units", meta.unitId ?? "");
            }
        }
    };

    const selectedMeta: CategoryParameter | undefined =
        parameter.name?.trim()
            ? availableParameters.find((p) => p.name === parameter.name)
            : undefined;

    const isNumeric = selectedMeta?.dataType === ParameterDataType.INTEGER || selectedMeta?.dataType === ParameterDataType.DOUBLE;
    const isDouble = selectedMeta?.dataType === ParameterDataType.DOUBLE;
    // DOUBLE: one or more allowed units (user picks one). INTEGER: single optional unit.
    const allowedUnits: string[] = isDouble
        ? allowedUnitsFor(selectedMeta)
        : (selectedMeta?.unitId ? [selectedMeta.unitId] : []);
    const selectedUnit = parameter.units ?? pickDefaultUnit(allowedUnits);
    const unitName = (uid?: string) =>
        uid ? (units.find(u => u.id === uid)?.name ?? uid) : "";
    // INTEGER (and single-unit display) label.
    const unitLabel = isNumeric && !isDouble && selectedMeta?.unitId
        ? unitName(selectedMeta.unitId)
        : null;

    const canSave = !loading;

    // normalize selectable MULTI values (string[] or csv string)
    const multiSelected: string[] = React.useMemo(() => {
        const raw = parameter.value;
        if (Array.isArray(raw)) return raw as string[];
        if (typeof raw === "string" && raw.trim()) {
            return raw.split(",").map((s) => s.trim()).filter(Boolean);
        }
        return [];
    }, [parameter.value]);

    const selectionType: SelectionType =
        ((selectedMeta as any)?.selectionType as SelectionType) ?? SelectionType.SINGLE;

    const selectableOptions: string[] =
        ((selectedMeta as any)?.options as string[]) ??
        ((selectedMeta as any)?.selectables as string[]) ??
        [];

    return (
        <div className="inventory-parameter-row">
            {/* Parameter name */}
            <div className="col name-col">
                {isNew ? (
                    <select
                        className="bh-input"
                        value={parameter.name ?? ""}
                        onChange={handleNameChange}
                        disabled={loading}
                    >
                        <option value="">-- select --</option>
                        {availableParameters.map((meta) => (
                            <option key={meta.name} value={meta.name}>
                                {meta.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span className="bh-field">{parameter.name}</span>
                )}
            </div>

            {/* Description */}
            <div className="col desc-col">{selectedMeta?.description ?? "—"}</div>

            {/* Value */}
            <div className="col value-col">
                {/* BOOLEAN */}
                {selectedMeta?.dataType === ParameterDataType.BOOLEAN ? (
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                            type="checkbox"
                            disabled={loading}
                            checked={toBoolean(parameter.value)}
                            onChange={(e) => onFieldChange("value", e.target.checked)}
                        />
                        {toBoolean(parameter.value) ? "true" : "false"}
                    </label>
                ) : selectedMeta?.dataType === ParameterDataType.SELECTABLE ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {selectableOptions.length === 0 ? (
                            <div style={{ fontSize: 12, color: "#666" }}>No options defined.</div>
                        ) : selectionType === SelectionType.SINGLE ? (
                            <>
                                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "normal", fontSize: "0.95rem", color: "inherit", marginBottom: 0 }}>
                                    <input
                                        type="radio"
                                        name={`selectable-${parameter.name}`}
                                        disabled={loading}
                                        checked={(parameter.value ?? "") === ""}
                                        onChange={() => onFieldChange("value", "")}
                                    />
                                    None
                                </label>

                                {selectableOptions.map((opt) => (
                                    <label key={opt} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "normal", fontSize: "0.95rem", color: "inherit", marginBottom: 0 }}>
                                        <input
                                            type="radio"
                                            name={`selectable-${parameter.name}`}
                                            disabled={loading}
                                            checked={(parameter.value ?? "") === opt}
                                            onChange={() => onFieldChange("value", opt)}
                                        />
                                        {opt}
                                    </label>
                                ))}
                            </>
                        ) : (
                            <>
                                {selectableOptions.map((opt) => (
                                    <label key={opt} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "normal", fontSize: "0.95rem", color: "inherit", marginBottom: 0 }}>
                                        <input
                                            type="checkbox"
                                            disabled={loading}
                                            checked={multiSelected.includes(opt)}
                                            onChange={(e) => {
                                                const next = e.target.checked
                                                    ? [...multiSelected, opt]
                                                    : multiSelected.filter((v) => v !== opt);
                                                onFieldChange("value", next);
                                            }}
                                        />
                                        {opt}
                                    </label>
                                ))}
                            </>
                        )}
                    </div>
                ) : selectedMeta?.dataType === ParameterDataType.DATE ? (
                    // DATE: native picker yields ISO yyyy-MM-dd, which the backend
                    // parses into a LocalDate.
                    <input
                        className="bh-input"
                        type="date"
                        value={parameter.value ?? ""}
                        onChange={(e) => onFieldChange("value", e.target.value)}
                        disabled={loading}
                    />
                ) : (
                    // STRING / INTEGER / DOUBLE
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <input
                            className="bh-input"
                            type="text"
                            value={parameter.value ?? ""}
                            onChange={(e) => onFieldChange("value", e.target.value)}
                            disabled={loading}
                        />
                        {isDouble && allowedUnits.length > 0 ? (
                            // DOUBLE: user picks which allowed unit the value is in.
                            <select
                                className="bh-input"
                                value={selectedUnit}
                                onChange={(e) => onFieldChange("units", e.target.value)}
                                disabled={loading || allowedUnits.length === 1}
                                style={{ width: "auto" }}
                            >
                                {allowedUnits.map((uid) => (
                                    <option key={uid} value={uid}>{unitName(uid)}</option>
                                ))}
                            </select>
                        ) : unitLabel ? (
                            <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                                {unitLabel}
                            </span>
                        ) : null}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="col action-col">
                <button
                    onClick={async () => {
                        setSaveError(null);
                        try {
                            await onSave();
                        } catch (err) {
                            logApiError(err);
                            setSaveError(getUserMessage(err));
                        }
                    }}
                    disabled={!canSave}
                >
                    Save
                </button>
                <button onClick={() => { setSaveError(null); onCancel(); }} disabled={loading}>
                    Cancel
                </button>
                {saveError && (
                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{saveError}</div>
                )}
            </div>
        </div>
    );
};

export default InventoryParameterForm;